/**
 * Judgment Index Store
 *
 * Manages Judgment Index profiles. Supabase is the source of truth; local
 * state is a cache reconstructible from the DB.
 *
 * Scores are stored as a JSONB payload in the `dimensions` column so the
 * shape can evolve without a migration. The `overall_capacity` column holds
 * the mean of the 6 primary IES scores as a single composite index.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  generateUUID,
} from '../../lib/supabaseSync';
import type {
  JudgmentIndexProfile,
  JudgmentProfileInput,
  JudgmentIES,
  JudgmentIESBands,
  JudgmentBalance,
  WorkSideDetail,
  SelfSideDetail,
} from '../../types/prioritization/judgmentIndex';
import { overallScore, emptyIES, emptyBalance } from '../../lib/judgmentIndexModel';

const TABLE = 'judgment_index_profiles';

type ScoresPayload = {
  workSide: JudgmentIES;
  selfSide: JudgmentIES;
  balance: JudgmentBalance;
  judgmentType?: number | null;
  judgmentTypeName?: string | null;
  workSideBands?: JudgmentIESBands | null;
  selfSideBands?: JudgmentIESBands | null;
  workSideDetail?: WorkSideDetail | null;
  selfSideDetail?: SelfSideDetail | null;
};

function rowToProfile(row: Record<string, unknown>): JudgmentIndexProfile {
  const scores = (row.dimensions ?? {}) as Partial<ScoresPayload>;
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    subjectUserId:   (row.subject_user_id  as string | null) ?? null,
    subjectName:     (row.subject_name     as string)        ?? '',
    subjectEmail:    (row.subject_email    as string | null) ?? null,
    assessedOn:      (row.assessed_on      as string | null) ?? null,
    source:          (row.source as JudgmentIndexProfile['source']) ?? 'manual',
    workSide:        scores.workSide        ?? emptyIES(),
    selfSide:        scores.selfSide        ?? emptyIES(),
    balance:         scores.balance         ?? emptyBalance(),
    judgmentType:    scores.judgmentType    ?? null,
    judgmentTypeName: scores.judgmentTypeName ?? null,
    workSideBands:   scores.workSideBands   ?? null,
    selfSideBands:   scores.selfSideBands   ?? null,
    workSideDetail:  scores.workSideDetail  ?? null,
    selfSideDetail:  scores.selfSideDetail  ?? null,
    notes:   (row.notes      as string | null) ?? null,
    createdBy: (row.created_by as string | null) ?? null,
    createdAt: (row.created_at as string)        ?? new Date().toISOString(),
    updatedAt: (row.updated_at as string)        ?? new Date().toISOString(),
  };
}

function inputToRow(
  input: JudgmentProfileInput,
  workspaceId: string,
  createdBy: string | null,
): Record<string, unknown> {
  const scores: ScoresPayload = {
    workSide: input.workSide,
    selfSide:  input.selfSide,
    balance:   input.balance,
    judgmentType:     input.judgmentType     ?? null,
    judgmentTypeName: input.judgmentTypeName ?? null,
    workSideBands:    input.workSideBands    ?? null,
    selfSideBands:    input.selfSideBands    ?? null,
    workSideDetail:   input.workSideDetail   ?? null,
    selfSideDetail:   input.selfSideDetail   ?? null,
  };
  return {
    workspace_id:   workspaceId,
    subject_user_id: input.subjectUserId ?? null,
    subject_name:   input.subjectName.trim(),
    subject_email:  input.subjectEmail?.trim() || null,
    assessed_on:    input.assessedOn || null,
    source:         input.source ?? 'manual',
    dimensions:     scores,
    overall_capacity: overallScore(input.workSide, input.selfSide),
    notes:          input.notes?.trim() || null,
    created_by:     createdBy,
  };
}

interface JudgmentIndexState {
  profiles: JudgmentIndexProfile[];
  isLoading: boolean;
  error: string | null;

  fetchProfiles: () => Promise<void>;
  createProfile: (input: JudgmentProfileInput, createdBy?: string | null) => Promise<{ profile?: JudgmentIndexProfile; error?: string }>;
  createMany: (inputs: JudgmentProfileInput[], createdBy?: string | null) => Promise<{ created: number; error?: string }>;
  updateProfile: (id: string, input: JudgmentProfileInput) => Promise<{ profile?: JudgmentIndexProfile; error?: string }>;
  deleteProfile: (id: string) => Promise<{ error?: string }>;

  getProfileById: (id: string) => JudgmentIndexProfile | undefined;
  getProfileForUser: (userId: string) => JudgmentIndexProfile | undefined;
}

export const useJudgmentIndexStore = create<JudgmentIndexState>()((set, get) => ({
  profiles: [],
  isLoading: false,
  error: null,

  fetchProfiles: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) { set({ profiles: [], isLoading: false }); return; }
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from(TABLE).select('*').eq('workspace_id', workspaceId).order('created_at', { ascending: false });
    if (error) { set({ isLoading: false, error: error.message }); return; }
    set({ profiles: (data ?? []).map((r) => rowToProfile(r as Record<string, unknown>)), isLoading: false });
  },

  createProfile: async (input, createdBy = null) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { error: 'No workspace selected.' };
    if (!input.subjectName?.trim()) return { error: 'A name is required.' };
    const row = { id: generateUUID(), ...inputToRow(input, workspaceId, createdBy) };
    const { data, error } = await supabase.from(TABLE).insert(row).select().single();
    if (error) return { error: error.message };
    const profile = rowToProfile(data as Record<string, unknown>);
    set((s) => ({ profiles: [profile, ...s.profiles] }));
    return { profile };
  },

  createMany: async (inputs, createdBy = null) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { created: 0, error: 'No workspace selected.' };
    const valid = inputs.filter((i) => i.subjectName?.trim());
    if (valid.length === 0) return { created: 0, error: 'No valid rows to import.' };
    const rows = valid.map((i) => ({ id: generateUUID(), ...inputToRow(i, workspaceId, createdBy) }));
    const { data, error } = await supabase.from(TABLE).insert(rows).select();
    if (error) return { created: 0, error: error.message };
    const created = (data ?? []).map((r) => rowToProfile(r as Record<string, unknown>));
    set((s) => ({ profiles: [...created, ...s.profiles] }));
    return { created: created.length };
  },

  updateProfile: async (id, input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { error: 'No workspace selected.' };
    if (!input.subjectName?.trim()) return { error: 'A name is required.' };
    const patch = inputToRow(input, workspaceId, null);
    delete patch.created_by;
    const { data, error } = await supabase.from(TABLE).update(patch).eq('id', id).select().single();
    if (error) return { error: error.message };
    const profile = rowToProfile(data as Record<string, unknown>);
    set((s) => ({ profiles: s.profiles.map((p) => (p.id === id ? profile : p)) }));
    return { profile };
  },

  deleteProfile: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { error: 'Supabase unavailable.' };
    const { error } = await supabase.from(TABLE).delete().eq('id', id);
    if (error) return { error: error.message };
    set((s) => ({ profiles: s.profiles.filter((p) => p.id !== id) }));
    return {};
  },

  getProfileById: (id) => get().profiles.find((p) => p.id === id),
  getProfileForUser: (userId) => get().profiles.find((p) => p.subjectUserId === userId),
}));
